import React, { useEffect, useState } from 'react';
import { FormControl, Button, Typography } from '@mui/material';
import styles from '../../../index.module.scss';
import BaseModal from '../../shared/components/base.modal';
import apiClient from '../../shared/auth.interceptor';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { IFirm } from '../../shared/redux/firmSlicer';
import { Company } from '../clients/clients-list';
import { AppDispatch, RootState } from '../../../store';
import TimeZoneAutocomplete, { UsTimeZone } from '../../shared/components/time-zone-autocomplete';
import { errorToast } from '../../shared/toast.service';
import { useNavigate } from 'react-router-dom';
import { syncFirms } from '../../shared/redux/syncFirms';

interface SetupClientModalProps {
  open: boolean;
  client: Company;
  handleClose: () => void;
  handleSuccess: () => void;
}

const SetupClientModal: React.FC<SetupClientModalProps> = ({ open, handleClose, handleSuccess, client }) => {
  const navigate = useNavigate();
  const currentFirm = useSelector((state: RootState) => state.currentFirm.currentFirm);
  const [selectedTimeZone, setSelectedTimeZone] = useState<UsTimeZone | null>(null);
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    setSelectedTimeZone(null);
  }, [open, client.realmId]);

  const handleUpdate = async () => {
    if (saving || !selectedTimeZone || !currentFirm?._id || !currentFirm.name ||
        !currentFirm.companies.some(company => company.realmId === client.realmId)) return;
    setSaving(true);
    try {
      const response = await apiClient.put<IFirm[]>(`/v1/firm/clients/setup/${currentFirm._id}`, {
        realmId: client.realmId,
        // The firm was selected before OAuth. Its name also marks setup complete
        // for the existing Clients flow; this endpoint does not move companies.
        firmName: currentFirm.name,
        timezone: selectedTimeZone.value
      });
      dispatch(syncFirms(response.data));
      toast.success('Client setup completed');
      handleSuccess();
      navigate('/user/clients');
    } catch {
      errorToast('Failed to set up client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal open={open} handleClose={handleClose}>
      <img alt="Logo" src="/logo120.svg" />
      <p className="my-4">Set the client's time zone</p>
      <Typography variant="h6" sx={{ color: styles.mainGreen, textDecoration: 'underline' }} className="my-3">
        {client.companyName}
      </Typography>
      <FormControl fullWidth margin="normal" sx={{ border: 'none' }}>
        <TimeZoneAutocomplete selectedTimeZone={selectedTimeZone} onSelectTimeZone={setSelectedTimeZone} />
      </FormControl>
      <Button
        disabled={saving || !selectedTimeZone || !currentFirm?._id}
        onClick={handleUpdate}
        variant="contained"
        sx={{ mt: 2, width: '100%', bgcolor: styles.mainBlue }}
      >
        Save
      </Button>
    </BaseModal>
  );
};

export default SetupClientModal;
